/**
 * Retry and failure shapes shared by every client upload lane
 * (`upload-user-file.ts`, `upload-private-chat-file.ts`). Browser-bundled.
 */

import { sleep } from '@/lib/shared/utils/sleep'
import {
  isPermanentUploadMessage,
  isRetryableUploadError,
  UploadError,
} from './upload-error'

/**
 * Staging a few hundred files means a few hundred independent network legs, so
 * a transient blob/network blip is a near-certainty rather than an edge case —
 * and without a retry each one permanently loses that file from the selection.
 * Two attempts with a short backoff; a genuine 4xx (bad type, over size) is not
 * retried because the payload cannot change.
 */
const STAGING_MAX_ATTEMPTS = 3
const STAGING_RETRY_DELAY_MS = 600

/**
 * Browser-bundled (ten client importers): a hand-rolled loop keeps p-retry,
 * `@opentelemetry/api` and the observability core out of the client graph.
 * JITTERED backoff — staging runs a worker pool, so a blip that trips one
 * file usually trips several at once; a fixed delay marches them all back
 * onto the wire in lockstep and reproduces the pinch that caused it.
 *
 * Retries ONLY what `isRetryableUploadError` says can change: a 422 or 504
 * conversion verdict is final for these bytes and comes straight back.
 */
export async function withStagingRetry<T>(fn: () => Promise<T>): Promise<T> {
  let lastError: unknown
  // biome-ignore-start lint/performance/noAwaitInLoops: retries are sequential by definition
  // biome-ignore lint/plugin: retry-loop: allowed — browser-bundled upload path; the shared helper would pull p-retry and OpenTelemetry into the client graph
  for (let attempt = 1; attempt <= STAGING_MAX_ATTEMPTS; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error
      if (attempt === STAGING_MAX_ATTEMPTS || !isRetryableUploadError(error)) {
        break
      }
      const backoff = STAGING_RETRY_DELAY_MS * 2 ** (attempt - 1)
      await sleep(backoff * (0.5 + Math.random()))
    }
  }
  // biome-ignore-end lint/performance/noAwaitInLoops: retries are sequential by definition
  throw lastError
}

/**
 * Untyped Blob failures stay retryable (a generic "Internal Server Error"
 * from the store is a moment); only a verdict about the file itself — too
 * large, type not allowed — is final.
 */
export function blobStageFailure(
  error: unknown,
  file: File,
  pathname: string
): UploadError {
  if (error instanceof UploadError) {
    return error
  }
  return new UploadError(
    'blob_stage_failed',
    `Could not upload "${file.name || pathname}" — check your connection and try again`,
    { retryable: !isPermanentUploadMessage(error), cause: error }
  )
}
